
export interface ChecklistItem { id: string; pillar: string; requirement: string; applicability: string; riskWeight: string; evidence: string; status: string; notes: string; }

const FATF_PILLARS = [
  {
    name: 'Risk Assessment',
    requirements: [
      'Documented risk assessment covering clients, products, services, delivery channels, and geographic locations',
      'Risk assessment reviewed and updated at least annually or upon significant changes',
      'Risk ratings (Low/Medium/High) assigned to all clients and relationships',
      'Enhanced Due Diligence (EDD) procedures for high-risk clients',
      'Risk assessment informs compliance program design and resource allocation'
    ]
  },
  {
    name: 'Policies & Procedures',
    requirements: [
      'Written AML/ATF policies approved by senior management',
      'Procedures for client identification and verification (CDD/KYC)',
      'Procedures for ongoing monitoring of client activity',
      'Procedures for suspicious transaction detection and reporting',
      'Record retention policy (minimum 5 years)',
      'Procedures for high-risk country transactions'
    ]
  },
  {
    name: 'Training & Awareness',
    requirements: [
      'AML/ATF training provided to all relevant employees upon hire',
      'Refresher training conducted at least annually',
      'Training tailored to role (front-line, compliance, management)',
      'Training covers: red flags, reporting procedures, record keeping',
      'Training attendance records maintained'
    ]
  },
  {
    name: 'Monitoring & Controls',
    requirements: [
      'Automated transaction monitoring system in place',
      'Monitoring rules calibrated to risk profile',
      'Alert investigation procedures documented',
      'Escalation path for suspicious activity clearly defined',
      'Quality assurance program for monitoring effectiveness'
    ]
  },
  {
    name: 'Reporting',
    requirements: [
      'Suspicious Transaction Reports (STRs) filed within required timeframe',
      'Large Cash Transaction Reports (LCTRs) filed accurately and timely',
      'Electronic Funds Transfer Reports (EFTRs) filed as required',
      'Terrorist Property Reports filed immediately upon discovery',
      'Reporting quality monitored and errors corrected promptly'
    ]
  }
];

const FINTRAC_SPECIFIC: Record<string, string[]> = {
  banking: [
    'Large Cash Transaction Report (LCTR) — $10,000+ CAD',
    'Large Virtual Currency Transaction Report (LVCTR) — $10,000+ CAD equivalent',
    'Electronic Funds Transfer Report (EFTR) — $10,000+ CAD international',
    'Suspicious Transaction Report (STR) — reasonable grounds to suspect',
    'Terrorist Property Report (TPR) — immediate upon discovery',
    'Record keeping: client ID records, account records, transaction records'
  ],
  msb: [
    'Registration with FINTRAC maintained and updated',
    'Agent/broker due diligence procedures',
    'Travel rule compliance for virtual currency transfers',
    'Beneficial ownership verification for corporate clients',
    'Ongoing monitoring of agent transactions'
  ],
  securities: [
    'Know-Your-Client (KYC) obligations under NI 31-103',
    'Suitability assessment documentation',
    'Politically Exposed Person (PEP) identification',
    'Head of International Organization (HIO) identification',
    'Two-year compliance effectiveness review'
  ]
};

const RED_FLAGS = {
  client_behavior: [
    'Reluctance to provide identification or business documentation',
    'Unusual concern for privacy or secrecy',
    'Frequent changes to identification details',
    'Use of nominees or intermediaries without clear rationale',
    'Client structure unnecessarily complex for stated business purpose'
  ],
  transaction_patterns: [
    'Transactions inconsistent with stated business or income level',
    'Structuring transactions to avoid reporting thresholds',
    'Rapid movement of funds through accounts (layering)',
    'Transactions with high-risk jurisdictions without clear business purpose',
    'Use of multiple accounts without clear business rationale',
    'Frequent cash deposits just below reporting threshold'
  ],
  geographic: [
    'Transactions involving high-risk or sanctioned jurisdictions',
    'Client from high-risk jurisdiction with no local business presence',
    'Funds originating from or destined to jurisdictions with weak AML controls'
  ],
  product_service: [
    'Use of products/services inconsistent with client profile',
    'Request for unusual transaction types or settlement methods',
    'Preference for anonymity-enhancing products (cash, bearer instruments)'
  ]
};

function determineEvidence(req: string, sector: string): string {
  if (req.includes('report')) return 'Reporting logs, submission confirmations';
  if (req.includes('identif')) return 'ID documents, verification records';
  if (req.includes('monitor')) return 'Monitoring logs, alert records';
  if (req.includes('train')) return 'Training materials, attendance records';
  if (req.includes('policy')) return 'Policy documents, approval records';
  if (req.includes('risk')) return 'Risk assessment document';
  if (req.includes('assessment') && sector === 'securities') return 'Suitability forms, KYC documentation';
  return 'Relevant documentation';
}

export function generateChecklist(jurisdiction: string, sector: string, riskLevel: string, entityType: string, includeMonitoring: boolean, includeTraining: boolean): ChecklistItem[] {
  let checklist: ChecklistItem[] = [];
  let id = 1;

  FATF_PILLARS.forEach(pillar => {
    if (!includeTraining && pillar.name === 'Training & Awareness') return;
    if (!includeMonitoring && pillar.name === 'Monitoring & Controls') return;
    pillar.requirements.forEach(req => {
      checklist.push({
        id: `FATF-${String(id++).padStart(3, '0')}`,
        pillar: pillar.name,
        requirement: req,
        applicability: 'Universal',
        riskWeight: riskLevel === 'High' ? 'Critical' : riskLevel === 'Medium' ? 'Important' : 'Standard',
        evidence: determineEvidence(req, sector),
        status: '',
        notes: ''
      });
    });
  });

  if (jurisdiction === 'Canada' && FINTRAC_SPECIFIC[sector]) {
    FINTRAC_SPECIFIC[sector].forEach(req => {
      checklist.push({
        id: `FINTRAC-${String(id++).padStart(3, '0')}`,
        pillar: 'Jurisdiction-Specific',
        requirement: req,
        applicability: `Canada — ${sector}`,
        riskWeight: 'Critical',
        evidence: 'Transaction records, reporting logs',
        status: '',
        notes: ''
      });
    });
  }

  if (entityType === 'PEP') {
    checklist.push(
      { id: `PEP-${String(id++).padStart(3, '0')}`, pillar: 'Enhanced Due Diligence', requirement: 'Source of wealth verification for PEP', applicability: 'PEP clients', riskWeight: 'Critical', evidence: 'Wealth declaration, supporting documents', status: '', notes: '' },
      { id: `PEP-${String(id++).padStart(3, '0')}`, pillar: 'Enhanced Due Diligence', requirement: 'Senior management approval for PEP relationship', applicability: 'PEP clients', riskWeight: 'Critical', evidence: 'Approval memo, risk assessment', status: '', notes: '' },
      { id: `PEP-${String(id++).padStart(3, '0')}`, pillar: 'Enhanced Due Diligence', requirement: 'Enhanced ongoing monitoring for PEP (quarterly review)', applicability: 'PEP clients', riskWeight: 'Critical', evidence: 'Monitoring logs, review reports', status: '', notes: '' }
    );
  }

  if (entityType === 'Corporate') {
    checklist.push(
      { id: `CORP-${String(id++).padStart(3, '0')}`, pillar: 'Beneficial Ownership', requirement: 'Identify and verify all beneficial owners (25%+ ownership)', applicability: 'Corporate clients', riskWeight: 'Critical', evidence: 'Shareholder register, ownership structure chart', status: '', notes: '' },
      { id: `CORP-${String(id++).padStart(3, '0')}`, pillar: 'Beneficial Ownership', requirement: 'Screen beneficial owners against sanctions and PEP lists', applicability: 'Corporate clients', riskWeight: 'Critical', evidence: 'Screening results, match resolution', status: '', notes: '' }
    );
  }

  if (riskLevel === 'High') {
    checklist.forEach(item => { if (item.riskWeight === 'Standard') item.riskWeight = 'Important'; });
    checklist.push(
      { id: `HIGH-${String(id++).padStart(3, '0')}`, pillar: 'Enhanced Monitoring', requirement: 'Monthly transaction review for high-risk clients', applicability: 'High-risk rated', riskWeight: 'Critical', evidence: 'Review reports', status: '', notes: '' },
      { id: `HIGH-${String(id++).padStart(3, '0')}`, pillar: 'Enhanced Monitoring', requirement: 'Annual enhanced due diligence refresh', applicability: 'High-risk rated', riskWeight: 'Critical', evidence: 'EDD refresh documentation', status: '', notes: '' }
    );
  }

  return checklist;
}

export function detectRedFlags(clientDescription: string): { category: string; indicator: string; matchScore: number }[] {
  const flags: { category: string; indicator: string; matchScore: number }[] = [];
  const text = clientDescription.toLowerCase();
  Object.entries(RED_FLAGS).forEach(([category, items]) => {
    items.forEach(item => {
      const keywords = item.toLowerCase().split(' ').filter(w => w.length > 4);
      const matchCount = keywords.filter(kw => text.includes(kw)).length;
      if (matchCount >= 2) flags.push({ category, indicator: item, matchScore: matchCount });
    });
  });
  return flags.sort((a, b) => b.matchScore - a.matchScore);
}

export function exportCSV(checklist: ChecklistItem[]): string {
  const headers = ['ID', 'Pillar', 'Requirement', 'Applicability', 'Risk Weight', 'Evidence Required', 'Status', 'Notes'];
  const rows = checklist.map(item => [item.id, item.pillar, item.requirement, item.applicability, item.riskWeight, item.evidence, item.status, item.notes].map(f => `"${f.replace(/"/g, '""')}"`).join(','));
  return [headers.join(','), ...rows].join('\n');
}
